/**
 * Semantic VoiceBot experiment configuration (React-facing).
 * Orthogonal to operationalMode (production / bench / record / replay).
 * React UI must not reference vbClassifier, vbRenderFps, vbVerifiedMix,
 * vbLegacyGearPasser, vbLegacyVisemeFilter, vbLegacyConsonantHolder, or
 * vbFaceMorph.
 */

export const CLASSIFIER_MODES = Object.freeze({
  LEGACY: 'legacy',
  MATCHER: 'matcher',
  MATCHER_TEMPORAL: 'matcherTemporal',
});

const ALLOWED_CLASSIFIER_MODES = new Set(Object.values(CLASSIFIER_MODES));

export const RENDER_FPS = Object.freeze({
  DYNAMIC: null,
  FPS_30: 30,
  FPS_60: 60,
});

/**
 * Speech-input experiment: Standard vs Verified Speech (source-aware verified mix).
 * Boolean, not an operational mode. Preferred AVT start is Verified Speech.
 */
export const SPEECH_INPUT = Object.freeze({
  STANDARD: false,
  VERIFIED: true,
});

/**
 * Preferred Avatar Tuning / VoiceBot start when sessionStorage is empty.
 * URL omission still means iframe-native Standard / Face Morph Off / gates ON;
 * React passes these explicit values so the avatar iframe receives the flags.
 */
export const PREFERRED_ENGINE_START = Object.freeze({
  classifierMode: CLASSIFIER_MODES.LEGACY,
  renderFps: null,
  verifiedMix: true,
  faceMorph: true,
  legacyGearPasser: false,
  legacyVisemeFilter: false,
  legacyConsonantHolder: false,
});

/**
 * @returns {'legacy'|'matcher'|'matcherTemporal'}
 */
export function normalizeClassifierMode(value) {
  if (typeof value === 'string' && ALLOWED_CLASSIFIER_MODES.has(value)) {
    return value;
  }
  return CLASSIFIER_MODES.LEGACY;
}

/**
 * @returns {null|30|60} null = dynamic Vis
 */
export function normalizeRenderFps(value) {
  if (value === 30 || value === '30') return 30;
  if (value === 60 || value === '60') return 60;
  return null;
}

/**
 * @returns {boolean} false = Standard (omit activation param)
 */
export function normalizeVerifiedMix(value) {
  return value === true || value === 1 || value === '1';
}

/**
 * Experimental lower-face geometric morph. Off is the sprite blit
 * (omit activation param). On is avatar-only and reloads the iframe.
 * Preferred AVT start is On.
 */
export const FACE_MORPH = Object.freeze({
  OFF: false,
  ON: true,
});

/**
 * @returns {boolean} false = Sprite (omit activation param)
 */
export function normalizeFaceMorph(value) {
  return value === true || value === 1 || value === '1';
}

/**
 * Legacy articulation A/B. ON omits the URL param (iframe-native).
 * OFF is the preferred AVT start and only has an effect on the Legacy visual path.
 */
export const LEGACY_ARTICULATION = Object.freeze({
  ON: true,
  OFF: false,
});

function normalizeOnByDefault(value) {
  if (value === false || value === 0 || value === '0' || value === 'off' || value === 'OFF') {
    return false;
  }
  return true;
}

/**
 * @returns {boolean} true = ON (current legacy gear/Passer behaviour)
 */
export function normalizeLegacyGearPasser(value) {
  return normalizeOnByDefault(value);
}

/**
 * @returns {boolean} true = ON (current legacy filterViseme behaviour)
 */
export function normalizeLegacyVisemeFilter(value) {
  return normalizeOnByDefault(value);
}

/**
 * @returns {boolean} true = ON (current legacy consonantHolder behaviour)
 */
export function normalizeLegacyConsonantHolder(value) {
  return normalizeOnByDefault(value);
}

export function isLegacyArticulationApplicable(classifierMode) {
  return normalizeClassifierMode(classifierMode) === CLASSIFIER_MODES.LEGACY;
}

export function isExperimentalArticulationConfig(
  legacyGearPasser,
  legacyVisemeFilter,
  legacyConsonantHolder
) {
  return !normalizeLegacyGearPasser(legacyGearPasser)
    || !normalizeLegacyVisemeFilter(legacyVisemeFilter)
    || !normalizeLegacyConsonantHolder(legacyConsonantHolder);
}

export function snapshotLegacyArticulation(
  classifierMode,
  legacyGearPasser,
  legacyVisemeFilter,
  legacyConsonantHolder
) {
  const applicable = isLegacyArticulationApplicable(classifierMode);
  const gear = normalizeLegacyGearPasser(legacyGearPasser);
  const filter = normalizeLegacyVisemeFilter(legacyVisemeFilter);
  const holder = normalizeLegacyConsonantHolder(legacyConsonantHolder);
  return {
    legacyGearPasserRequested: gear,
    legacyGearPasserEnabled: gear,
    legacyGearPasserApplicable: applicable,
    legacyVisemeFilterRequested: filter,
    legacyVisemeFilterEnabled: filter,
    legacyVisemeFilterApplicable: applicable,
    legacyConsonantHolderRequested: holder,
    legacyConsonantHolderEnabled: holder,
    legacyConsonantHolderApplicable: applicable,
  };
}

export function isExperimentalEngineConfig(classifierMode, renderFps, verifiedMix, faceMorph) {
  return normalizeClassifierMode(classifierMode) !== CLASSIFIER_MODES.LEGACY
    || normalizeRenderFps(renderFps) != null
    || normalizeVerifiedMix(verifiedMix)
    || normalizeFaceMorph(faceMorph);
}
